import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Link,
  SimpleGrid,
  Text
} from "@chakra-ui/react"
import Footer from "../components/Footer"

const steps = [
  {
    label: "Identify",
    description:
      "We scan your entire portfolio in just a few seconds to identify high retrofit potential homes that will deliver the best return on your investment."
  },
  {
    label: "Analyse",
    description:
      "AI spots opportunities based on strong energy upgrade ROI, matches funding opportunities so you can focus your budget where it delivers greatest impact."
  },
  {
    label: "Activate",
    description:
      "We monitor portfolio performance and report impact, giving you credible carbon reduction figures and financial performance."
  }
]

const benefits = [
  {
    title: "Tackle Fuel Poverty at Scale",
    body: "Identify the homes where upgrades will have the greatest impact on energy bills, delivering meaningful cost savings for tenants."
  },
  {
    title: "Stay Compliant, Avoid Risk",
    body: "Meet EPC and decarbonisation targets with confidence. Reduce exposure to fines and regulatory pressure."
  },
  {
    title: "Maximise Your Budget",
    body: "Prioritise upgrades that deliver the greatest impact per £ spent. Build clear, data-backed investment plans across your portfolio."
  },
  {
    title: "Improve Tenant Comfort & Satisfaction",
    body: "Create warmer, healthier homes, supporting tenant wellbeing and improving satisfaction scores."
  },
  {
    title: "Lower Operational Costs, Enhanced Quality",
    body: "Improve long-term asset quality and financial viability. Reduce operational costs while maintaining high-quality homes."
  },
  {
    title: "Deliver Net Zero with Confidence",
    body: "Plan a clear pathway to zero operational carbon. Futureproof your portfolio and align with 2050 targets with clarity."
  },
  {
    title: "Unlock Funding & Deliver at Scale",
    body: "Access funding opportunities with credible, data-driven retrofit strategies, making large-scale upgrades both viable and deliverable."
  },
  {
    title: "Model Returns",
    body: "Model smart tariffs and retrofit scenarios to unlock affordable capital, generate financial returns, and reduce energy bills for your tenants."
  }
]

const SocialLandlords: React.FC = () => (
  <Box>
    {/* Hero */}
    <Box bg="brand.700">
      <Container maxW="container.md" py={24} textAlign="center">
        <Heading as="h1" size="5xl" color="white" mb={6}>
          Social Landlords
        </Heading>
        <Text fontSize="xl" color="whiteAlpha.900" maxW={700} m="auto">
          GreenFlip helps you deliver energy upgrades that reduce fuel poverty,
          meet regulation, and protect long-term housing stock value while
          maximising your budget.
        </Text>
      </Container>
    </Box>

    {/* How it works */}
    <Container maxW="container.md" py={20}>
      <HStack gap={6} align="stretch" flexWrap={["wrap", "nowrap"]}>
        {steps.map((step, i) => (
          <Box
            key={step.label}
            flex={1}
            minW={["100%", "0"]}
            bg="gray.50"
            p={8}
            borderRadius="2xl"
            borderTop="4px solid"
            borderColor="brand.600"
          >
            <Text
              fontSize="xs"
              fontWeight="bold"
              color="brand.600"
              textTransform="uppercase"
              letterSpacing="wide"
              mb={2}
            >
              {String(i + 1).padStart(2, "0")}
            </Text>
            <Heading as="h3" size="lg" mb={3} color="green.800">
              {step.label}
            </Heading>
            <Text color="gray.600">{step.description}</Text>
          </Box>
        ))}
      </HStack>
    </Container>

    {/* Benefits */}
    <Box bg="gray.50">
      <Container maxW="container.md" py={20}>
        <Heading
          as="h2"
          size="3xl"
          mb={12}
          textAlign="center"
          color="green.800"
        >
          Key Benefits for Social Landlords
        </Heading>
        <SimpleGrid columns={[1, 2]} gap={6}>
          {benefits.map((b) => (
            <Box key={b.title} bg="white" p={6} borderRadius="xl" shadow="sm">
              <Heading as="h3" size="md" mb={3} color="green.800">
                {b.title}
              </Heading>
              <Text color="gray.600" fontSize="sm" lineHeight="tall">
                {b.body}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Container>
    </Box>

    {/* CTA */}
    <Box bg="brand.700">
      <Container maxW="container.md" py={20} textAlign="center">
        <Heading as="h2" size="3xl" color="white" mb={6}>
          Maximise impact across your housing stock
        </Heading>
        <Text color="whiteAlpha.900" fontSize="lg" maxW={600} m="auto" mb={8}>
          Reach out to maximise tenant comfort, cut costs, and meet your
          regulatory goals.
        </Text>
        <Link href="/contact">
          <Button
            bg="white"
            color="brand.700"
            size="lg"
            _hover={{ bg: "gray.100" }}
          >
            Get in touch
          </Button>
        </Link>
      </Container>
    </Box>

    <Footer />
  </Box>
)

export default SocialLandlords
